import React, { useState } from 'react';
import {
  Box, TextField, Button, FormControl, InputLabel, Select, MenuItem,
  CircularProgress,
} from '@mui/material';
import DrawerShell from './_drawer/DrawerShell';
import DrawerHeader from './_drawer/DrawerHeader';
import useResponsiveAnchor from './_drawer/useResponsiveAnchor';
import api from '../../utils/api';
import { RADIUS_LIBRARIAN as RADIUS } from '../../theme/tokens';

const DEFAULT_FORM = {
  seat_no: '',
  locker_no: '',
  note_text: '',
  note_type: 'general',
  is_important: false,
};

function StudentNoteDrawer({ open, onClose, studentId, onSaved, showSnackbar }) {
  const anchor = useResponsiveAnchor();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    setForm(DEFAULT_FORM);
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.note_text.trim() && !form.seat_no.trim() && !form.locker_no.trim()) {
      showSnackbar('Please enter a note or seat/locker number', 'error');
      return;
    }

    try {
      setSaving(true);
      await api.post(`/librarian/students/${studentId}/notes`, {
        note_text: form.note_text ||
          `Assigned: ${form.seat_no ? `Seat ${form.seat_no}` : ''}${form.seat_no && form.locker_no ? ', ' : ''}${form.locker_no ? `Locker ${form.locker_no}` : ''}`,
        note_type: form.note_type,
        is_important: form.is_important,
        seat_no: form.seat_no || null,
        locker_no: form.locker_no || null,
      });
      showSnackbar('Note added successfully', 'success');
      setForm(DEFAULT_FORM);
      onSaved();
      onClose();
    } catch (err) {
      showSnackbar('Failed to add note', 'error');
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button onClick={handleClose} disabled={saving} sx={{ borderRadius: RADIUS.md }}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={saving}
        fullWidth
        sx={{ borderRadius: RADIUS.md, background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)' }}
      >
        {saving ? <CircularProgress size={20} color="inherit" /> : 'Add Note'}
      </Button>
    </Box>
  );

  return (
    <DrawerShell open={open} onClose={handleClose} anchor={anchor} ariaLabel="Add note" footer={footer}>
      <DrawerHeader title="Add Note" onClose={handleClose} />
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Seat No."
            placeholder="e.g., A-12"
            value={form.seat_no}
            onChange={(e) => setForm({ ...form, seat_no: e.target.value })}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Locker No."
            placeholder="e.g., L-05"
            value={form.locker_no}
            onChange={(e) => setForm({ ...form, locker_no: e.target.value })}
            size="small"
            sx={{ flex: 1 }}
          />
        </Box>

        <TextField
          multiline
          rows={4}
          placeholder="Write a note about this student..."
          value={form.note_text}
          onChange={(e) => setForm({ ...form, note_text: e.target.value })}
          fullWidth
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={form.note_type}
              onChange={(e) => setForm({ ...form, note_type: e.target.value })}
              label="Type"
            >
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="payment">Payment</MenuItem>
              <MenuItem value="behavior">Behavior</MenuItem>
              <MenuItem value="complaint">Complaint</MenuItem>
              <MenuItem value="praise">Praise</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant={form.is_important ? 'contained' : 'outlined'}
            color={form.is_important ? 'error' : 'inherit'}
            size="small"
            onClick={() => setForm({ ...form, is_important: !form.is_important })}
            sx={{ borderRadius: RADIUS.md }}
          >
            {form.is_important ? 'Important' : 'Normal'}
          </Button>
        </Box>
      </Box>
    </DrawerShell>
  );
}

export default StudentNoteDrawer;
